import { DesignSourceEnum } from '@insights/insights-data-provider';
import { captureMessage } from '@sentry/react';
import { getCountry, getLanguage, getLocale, getLocaleFromPath } from 'i18n/i18nUtils';
import { AppRoutesEnum } from 'routes';
import { QUERY_PARAMS, SPR_REGEX, SprCodes, VPC_REGEX } from 'utils/constants';
import { isLighthouse, isUpptacka } from 'utils/platform';
import useQuery from 'utils/useQuery';

const getVpcId = (hash: string) => {
  const vpcId = hash.match(VPC_REGEX)?.[1];
  return vpcId && vpcId.length === 6 ? vpcId : undefined;
};

export const getSprId = (hash: string) => {
  const sprId = hash.match(SPR_REGEX)?.[1];
  return sprId && sprId.length === 8 ? `S${sprId}` : undefined;
};

export const getUrl = (url: string) => {
  return new URL(url, window.location.href).href;
};

export const isValidUrl = (oldUrl: URL, newUrlString: string): boolean => {
  return newUrlString.startsWith(oldUrl.origin);
};

/**
 * Handle app redirects, will be used when redirecting hash ids like sprs, vpcs etc.
 */
export const redirects = () => {
  const url = new URL(window.location.href);

  const designCodeFromQuery = useQuery(QUERY_PARAMS.DESIGN_CODE);

  const storageDesignCodeFromQuery = useQuery(QUERY_PARAMS.STORAGE_DESIGN_CODE);

  const { hash } = url;

  const vpcId = getVpcId(hash);

  const sprId = getSprId(hash);

  const validId = vpcId || (sprId && SprCodes[sprId]);

  if (sprId && !SprCodes[sprId]) {
    captureMessage('Incompatible SPR ID', (scope) => {
      scope.setExtra('SPR', sprId);
      return scope;
    });
  }

  /**
   * Has ID from hash such as spr or vpc.
   */
  if (validId) {
    // VPC http://localhost:3000/?uiPlatform=web&locale=sv-SE#RJ45FP
    // SPR http://localhost:3000/?uiPlatform=web&locale=sv-SE#S19424042
    // designCode http://localhost:3000/?uiPlatform=web&locale=sv-SE&designCode=RJ45FP
    // storageDesignCode http://localhost:3000/?uiPlatform=web&locale=sv-SE&storageDesignCode=KPR4M7

    // Should it actually be searchBar for all other cases when not SPR?
    const designSource = sprId ? DesignSourceEnum.spr : DesignSourceEnum.searchBar;
    url.searchParams.set(QUERY_PARAMS.DESIGN_CODE, validId);
    url.searchParams.set(QUERY_PARAMS.VPC_SOURCE, designSource);
    const { search } = url;
    const newUrlString = getUrl(`${search}#${AppRoutesEnum.PLANNER}`);
    if (isValidUrl(url, newUrlString)) {
      if (!sprId) {
        captureMessage('Arrived from shortUrl', (scope) => {
          scope.setExtras({
            modifiedUrl: newUrlString,
            originalUrl: window.location.href
          });
          return scope;
        });
      }
      window.location.href = newUrlString;
    }
    /**
     * Has no valid hash ID but has query param.
     */
  } else if (
    !validId &&
    (designCodeFromQuery || storageDesignCodeFromQuery) &&
    !window.location.href.includes(AppRoutesEnum.PLANNER) &&
    !window.location.href.includes(AppRoutesEnum.SUMMARY)
  ) {
    const { search } = url;
    const newUrlString = getUrl(`${search}#${AppRoutesEnum.PLANNER}`);
    if (isValidUrl(url, newUrlString)) {
      window.location.href = newUrlString;
    }
  }

  const lighthouse = isLighthouse();
  if (lighthouse) {
    const { search } = url;
    const newUrlString = getUrl(`${search}#${AppRoutesEnum.PLANNER}`);
    if (isValidUrl(url, newUrlString)) {
      window.location.href = newUrlString;
    }
  }
};

const getNewPath = (newLocale: string, currentLocale: string) => {
  let newPath = window.location.pathname;

  const currentCountry = getCountry(currentLocale).toLocaleLowerCase();
  const countryRegex = new RegExp(`${currentCountry}`);
  newPath = newPath.replace(countryRegex, getCountry(newLocale).toLocaleLowerCase());

  const currentLanguage = getLanguage(currentLocale).toLocaleLowerCase();
  const languageRegex = new RegExp(`${currentLanguage}(?!.*${currentLanguage})`);
  newPath = newPath.replace(languageRegex, getLanguage(newLocale).toLocaleLowerCase());

  return newPath;
};

export const redirectToNewLocale = (locale: string) => {
  const isUpptackaSession = isUpptacka();
  const currentLocale = getLocale();

  if (isUpptackaSession || locale === currentLocale) {
    return;
  }

  const url = new URL(window.location.href);

  if (getLocaleFromPath()) {
    window.location.pathname = getNewPath(locale, currentLocale);
  } else {
    url.searchParams.set(QUERY_PARAMS.LOCALE, locale);
    window.location.href = url.href;
  }
};
